import { useEffect, useState } from 'react';
import { AlertBanner } from '@insio/ui-toolkit';
import { nowInstant } from '@insio/dates';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import type { PendingPlanUpgrade } from '../hooks/use-pending-plan-upgrade';

/** The longest delay a browser timer takes (about 24.8 days). */
const MAX_TIMER_MS = 2_147_483_647;

interface PendingPlanUpgradeNoticeProps {
  upgrade: PendingPlanUpgrade | null;
}

/** A credit upgrade waiting for its invoice, with the way to pay it; gone once Stripe drops it. */
export function PendingPlanUpgradeNotice({ upgrade }: PendingPlanUpgradeNoticeProps) {
  const t = useFeatureTranslation('credits.billing.pendingUpgrade');
  const expiresAtMs = upgrade?.expiresAt.epochMilliseconds ?? null;
  const [, rerender] = useState(0);

  // Re-render when the upgrade expires, so a page left open hides it on time.
  useEffect(() => {
    if (expiresAtMs === null) return;
    const remainingMs = expiresAtMs - nowInstant().epochMilliseconds;
    if (remainingMs <= 0) return;
    const timer = setTimeout(() => rerender(n => n + 1), Math.min(remainingMs + 1, MAX_TIMER_MS));
    return () => clearTimeout(timer);
  }, [expiresAtMs]);

  if (!upgrade || expiresAtMs === null || expiresAtMs <= nowInstant().epochMilliseconds) {
    return null;
  }

  return (
    <AlertBanner
      variant='info'
      size='sm'
      icon='clock'
      testId='pending-plan-upgrade-notice'
      title={t('title', { planName: upgrade.planName })}
    >
      {upgrade.invoiceUrl && (
        <a
          href={upgrade.invoiceUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='text-sm font-medium text-info-text underline hover:opacity-80'
        >
          {t('payInvoice')}
        </a>
      )}
    </AlertBanner>
  );
}
